import { useState, useCallback, useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { Modal } from "@/shared/components/Modal";
import { Button } from "@/shared/components/form/Button";
import { AddAccountForm } from "./AddAccountForm";
import type { NewPublicAccountFormData } from "../types/new-public-account.types";
import { PublicAccountPaymentMethod } from "../types/public-accounts-api.types";
import {
  validateNewPublicAccountForm,
  type NewPublicAccountFormErrors,
} from "../utils/validateNewPublicAccountForm";
import { mapPublicAccountDetailToFormData } from "../utils/mapPublicAccountDetailToFormData";
import { publicAccountsService } from "../services/publicAccounts.service";
import useTranslations from "@/shared/lang/useTranslations";

function getDefaultFormData(): NewPublicAccountFormData {
  return {
    payment_method: PublicAccountPaymentMethod.BANK,
    country_code: "",
    currency: "",
    internal_alias: "",
    notes: "",
    bank_name: "",
    account_number: "",
    account_type: "",
    wallet_address: "",
    network: "",
    attachments: [],
    methodFields: {},
  };
}

type AddAccountModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: NewPublicAccountFormData, accountId?: string) => void | Promise<void>;
  accountId?: string | null;
};

export function AddAccountModal({
  isOpen,
  onClose,
  onSubmit,
  accountId = null,
}: AddAccountModalProps) {
  const { t } = useTranslations("accountsWallets");
  const [formData, setFormData] = useState<NewPublicAccountFormData>(getDefaultFormData);
  const [errors, setErrors] = useState<NewPublicAccountFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const isEditMode = Boolean(accountId);

  useEffect(() => {
    if (!isOpen || !accountId) return;
    setIsLoadingEdit(true);
    setFormData(getDefaultFormData());
    setErrors({});
    publicAccountsService
      .fetchById(accountId)
      .then((account) => {
        setFormData(mapPublicAccountDetailToFormData(account));
      })
      .catch(() => {
        setErrors({ country_code: t("detail_error_load") });
      })
      .finally(() => {
        setIsLoadingEdit(false);
      });
  }, [isOpen, accountId, t]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setFormData(getDefaultFormData());
      setErrors({});
      onClose();
    }
  }, [onClose, isSubmitting]);

  const handleSubmit = useCallback(async () => {
    const currentData = formDataRef.current;
    const validationErrors = validateNewPublicAccountForm(currentData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setIsSubmitting(true);
    try {
      if (isEditMode && accountId) {
        await onSubmit?.(currentData, accountId);
      } else {
        await onSubmit?.(currentData);
      }
      setFormData(getDefaultFormData());
      onClose();
    } catch {
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit, onClose, isEditMode, accountId]);

  const footer = (
    <div className="flex flex-col space-y-4 w-full pt-1">
      <Button
        type="button"
        variant="primary"
        onClick={handleSubmit}
        disabled={isSubmitting || isLoadingEdit}
        isLoading={isSubmitting}
        className="w-full py-3.5 rounded-xl font-bold shadow-lg shadow-green-500/20 active:scale-[0.98]"
      >
        {isEditMode ? t("modal_update_button") : t("modal_create_button")}
      </Button>
      <button
        type="button"
        onClick={handleClose}
        disabled={isSubmitting}
        className="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 text-sm font-medium transition-colors"
      >
        {t("modal_cancel")}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? t("modal_edit_title") : t("modal_add_title")}
      size="md-wide"
      footer={footer}
    >
      {isLoadingEdit ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-green-500" />
          <p className="text-sm text-slate-600 dark:text-slate-400">{t("detail_loading")}</p>
        </div>
      ) : (
        <AddAccountForm
          value={formData}
          onChange={setFormData}
          errors={errors}
          disabled={isSubmitting}
          isEditMode={isEditMode}
        />
      )}
    </Modal>
  );
}
